import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings → Easeedesk → Ticket subcategories and operations.
 *
 * Perechile vechi: `settings/easeedesk/ticket_subcategories_settings`,
 * `subcategoryList`, `get_subcategory`, `subcategoryDetails` +
 * `getSubcategoryOperations`, si cele opt scrieri ale lor.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface SubcategoryCategory {
  ID: number
  category_name: string
}

export interface SubcategoriesShell {
  title: string
  moduleUrl: string
  categories: SubcategoryCategory[]
}

export interface SubcategoryRow {
  ID: number
  subcategory_name: string
  active: number
  category_id: number
  category_name: string
  operations: number
  DT_RowId: string
}

export interface SubcategoryTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: SubcategoryRow[]
}

export interface SubcategoryDetails {
  ID: number
  subcategory_name: string
  active: number
  category_id: number
  category_name: string
}

export interface Operation {
  ID: number
  operation_name: string
  estimate_effort: number
  active: number
}

export interface OperationsPage {
  subcategory: number
  operations: Operation[]
}

export interface SubcategoryTableRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

/** Aceiasi parametri pe care ii trimitea DataTables pe `subcategoryList`. */
export function subcategoryTableParams(r: SubcategoryTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export function subcategoriesShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_subcategories'],
    queryFn: () => apiGet<SubcategoriesShell>('/settings/easeedesk/ticket_subcategories'),
  })
}

export function subcategoryTableQuery(r: SubcategoryTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_subcategories', 'table', r],
    queryFn: () =>
      apiGet<SubcategoryTablePage>(`/settings/easeedesk/ticket_subcategories/table?${subcategoryTableParams(r).toString()}`),
  })
}

export function subcategoryQuery(subcategory: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_subcategories', 'one', subcategory],
    queryFn: () => apiGet<SubcategoryDetails>(`/settings/easeedesk/ticket_subcategories/${subcategory}`),
    enabled: subcategory > 0,
  })
}

export function subcategoryOperationsQuery(subcategory: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_subcategories', 'operations', subcategory],
    queryFn: () => apiGet<OperationsPage>(`/settings/easeedesk/ticket_subcategories/${subcategory}/operations`),
    enabled: subcategory > 0,
  })
}

export function operationQuery(operation: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_operations', operation],
    queryFn: () => apiGet<Operation>(`/settings/easeedesk/ticket_operations/${operation}`),
    enabled: operation > 0,
  })
}

export function createSubcategory(category: number, subcategory_name: string) {
  return apiPost<{ ok?: boolean; ID?: number }>('/settings/easeedesk/ticket_subcategories', {
    category,
    subcategory_name,
  })
}

export function saveSubcategory(subcategory: number, subcategory_name: string) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_subcategories/${subcategory}`, { subcategory_name })
}

export function activateSubcategory(subcategory: number, on: boolean) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_subcategories/${subcategory}/activate`, {
    status: on ? 'true' : 'false',
  })
}

export function removeSubcategory(subcategory: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_subcategories/${subcategory}/remove`, {})
}

export function createOperation(subcategory: number, operation_name: string, estimate_effort: number) {
  return apiPost<{ ok?: boolean; ID?: number }>(`/settings/easeedesk/ticket_subcategories/${subcategory}/operations`, {
    operation_name,
    estimate_effort,
  })
}

export function saveOperation(operation: number, operation_name: string, estimate_effort: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_operations/${operation}`, {
    operation_name,
    estimate_effort,
  })
}

export function activateOperation(operation: number, on: boolean) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_operations/${operation}/activate`, {
    status: on ? 'true' : 'false',
  })
}

export function removeOperation(operation: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_operations/${operation}/remove`, {})
}
